// ---------------------------------------------------------------------------
// Régua de Cobrança — cálculo da visão "Linha do tempo" (adendo 01).
//
// Tudo derivado NO CLIENTE da mesma resposta que alimenta kanban e fila —
// zero request. Helpers puros: o componente só desenha o que sai daqui.
//
// Regras que quebram se ignoradas (adendo §Regras de cálculo):
// 1. Só caso `OPEN` entra (fechado não tem compromisso pendente).
// 2. `next_action_at` nulo NUNCA vira "hoje" — vai só pro card à parte.
// 3. Fora da janela de ±14 dias, soma no ponto da borda (nada descartado).
// 4. Na métrica "boletos", contagem de casos do subtexto ignora open_count 0.
// ---------------------------------------------------------------------------

import { daysBetween } from '../../utils/dunningFormat';

export const TIMELINE_SPAN = 14; // ±14 dias
export const TIMELINE_DAYS = TIMELINE_SPAN * 2 + 1; // 29 colunas

// Geometria do SVG (viewBox 0 0 1000 260) — topo em 44 (não 24) pra pill
// "Hoje" não colidir com o ponto quando hoje é o dia de pico.
export const CHART = {
   x0: 40,
   x1: 980,
   baseY: 210,
   topY: 44,
   viewW: 1000,
   viewH: 260,
};

export function chartX(index) {
   return CHART.x0 + index * ((CHART.x1 - CHART.x0) / (TIMELINE_DAYS - 1));
}

export function chartY(value, niceMax) {
   return CHART.baseY - (value / niceMax) * (CHART.baseY - CHART.topY);
}

// Baldes na ordem do eixo (adendo §2). `hit` recebe o d JÁ clampado na janela.
export const TIMELINE_BUCKETS = [
   { key: 'late7', label: 'Atrasado 7d+', color: '#a01b1b', hit: (d) => d <= -7 },
   {
      key: 'late16',
      label: 'Atrasado 1 a 6d',
      color: '#ea580c',
      hit: (d) => d < 0 && d > -7,
   },
   {
      key: 'hoje',
      label: 'Acompanhar hoje',
      color: '#4098ef',
      hit: (d) => d === 0,
   },
   {
      key: 'soon16',
      label: 'A vencer 1 a 6d',
      color: '#7b8b99',
      hit: (d) => d > 0 && d < 7,
   },
   { key: 'soon7', label: 'A vencer 7d+', color: '#aeb6bd', hit: (d) => d >= 7 },
];

export function bucketOf(d) {
   return (
      TIMELINE_BUCKETS.find((bucket) => bucket.hit(d)) ??
      TIMELINE_BUCKETS[TIMELINE_BUCKETS.length - 1]
   );
}

function clampToWindow(d) {
   return Math.max(-TIMELINE_SPAN, Math.min(TIMELINE_SPAN, d));
}

// Contagem de casos exibível no subtexto: na métrica "boletos" um caso com
// open_count 0 não conta ("nunca 0 boletos · 2 casos" — regra 4).
function countableCases(cases, isBoletos) {
   return isBoletos
      ? cases.filter((row) => (row.open_count ?? 0) > 0)
      : cases;
}

// Modelo completo da visão. `metric`: 'boletos' | 'casos'.
export function buildTimelineModel({ rows, today, metric }) {
   const isBoletos = metric === 'boletos';
   const valueOf = (row) => (isBoletos ? row.open_count ?? 0 : 1);

   const live = rows.filter((row) => row.status === 'OPEN');
   const closedCount = rows.length - live.length;
   const withCommit = live.filter((row) => row.next_action_at);
   const withoutCommit = live.filter((row) => !row.next_action_at);

   // 29 posições: d ∈ [-14, +14]; compromissos fora da janela clampam na borda.
   const series = Array.from({ length: TIMELINE_DAYS }, (_, i) => ({
      d: i - TIMELINE_SPAN,
      value: 0,
      cases: 0,
      amount: 0,
   }));
   withCommit.forEach((row) => {
      const d = clampToWindow(daysBetween(today, row.next_action_at));
      const slot = series[d + TIMELINE_SPAN];
      slot.value += valueOf(row);
      slot.cases += 1;
      slot.amount += row.open_amount ?? 0;
   });

   const buckets = TIMELINE_BUCKETS.map((bucket) => {
      const hits = withCommit.filter((row) =>
         bucket.hit(clampToWindow(daysBetween(today, row.next_action_at)))
      );
      return {
         ...bucket,
         value: hits.reduce((acc, row) => acc + valueOf(row), 0),
         caseCount: countableCases(hits, isBoletos).length,
         amount: hits.reduce((acc, row) => acc + (row.open_amount ?? 0), 0),
      };
   });

   const sem = {
      value: withoutCommit.reduce((acc, row) => acc + valueOf(row), 0),
      caseCount: countableCases(withoutCommit, isBoletos).length,
      amount: withoutCommit.reduce(
         (acc, row) => acc + (row.open_amount ?? 0),
         0
      ),
   };

   // Escala Y arredondada pro próximo PAR (mínimo 2) — rótulos inteiros.
   const peak = Math.max(0, ...series.map((slot) => slot.value));
   let niceMax = 2;
   if (peak > 2) niceMax = peak % 2 ? peak + 1 : peak;

   return {
      isBoletos,
      live,
      closedCount,
      hasChart: live.length > 0,
      hasSem: sem.value > 0,
      series,
      buckets,
      sem,
      niceMax,
   };
}
